<script setup lang="ts" generic="T extends string | number">
import type { ISelectOption } from '@/interfaces';
import type { CSSProperties } from 'vue';
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue';

type SegmentedControlWidth = 'content' | 'full' | 'mobile-full';

const props = withDefaults(
  defineProps<{
    modelValue: T;
    options: ISelectOption<T>[];
    ariaLabel?: string;
    disabled?: boolean;
    width?: SegmentedControlWidth;
    size?: 'md' | 'sm';
    theme?: 'default' | 'gray-surface';
    label?: string;
  }>(),
  {
    ariaLabel: 'Segmented control',
    disabled: false,
    width: 'content',
    size: 'md',
    theme: 'default',
    label: undefined,
  },
);

const emit = defineEmits<{
  (event: 'update:modelValue', value: T): void;
}>();

const controlRef = useTemplateRef<HTMLElement>('controlRef');
const optionRefs = useTemplateRef<HTMLButtonElement[]>('optionRefs');
const indicatorStyle = ref<CSSProperties>({ opacity: 0 });
// the indicator is placed after mount, so its first placement must not slide in from the first option
const canAnimateIndicator = ref(false);
const selectedIndex = computed<number>(() => props.options.findIndex((option) => option.value === props.modelValue));
const isFullWidth = computed<boolean>(() => props.width === 'full');
const isMobileFullWidth = computed<boolean>(() => props.width === 'mobile-full');

let resizeObserver: ResizeObserver | null = null;
let updateFrameId: number | null = null;

function isSelected(value: T): boolean {
  return props.modelValue === value;
}

function isOptionDisabled(option: ISelectOption<T>): boolean {
  return props.disabled || option.selectable === false;
}

function onSelect(value: T): void {
  const option = props.options.find((opt) => opt.value === value);
  if (!option) return;
  if (isOptionDisabled(option) || isSelected(value)) return;
  emit('update:modelValue', value);
}

function getSelectedButton(): HTMLButtonElement | null {
  const index = selectedIndex.value;
  if (index < 0) return null;
  return optionRefs.value?.[index] ?? null;
}

function updateIndicatorPosition(): void {
  const selectedButton = getSelectedButton();
  // no measurable box: the control is hidden (closed popover) or not laid out yet
  if (!selectedButton?.offsetWidth) {
    indicatorStyle.value = { opacity: 0 };
    canAnimateIndicator.value = false;
    return;
  }

  // placing it for the first time — or again after it was hidden — must not slide in from the left
  if (indicatorStyle.value.opacity !== 1) canAnimateIndicator.value = false;

  indicatorStyle.value = {
    width: `${selectedButton.offsetWidth}px`,
    transform: `translate3d(${selectedButton.offsetLeft}px, 0, 0)`,
    opacity: 1,
  };

  if (canAnimateIndicator.value) return;
  // animate only from the next placement on — once this one has been painted
  requestAnimationFrame(() => {
    canAnimateIndicator.value = true;
  });
}

function requestIndicatorUpdate(): void {
  if (updateFrameId != null) {
    cancelAnimationFrame(updateFrameId);
  }

  updateFrameId = requestAnimationFrame(() => {
    updateFrameId = null;
    updateIndicatorPosition();
  });
}

function reconnectResizeObserver(): void {
  resizeObserver?.disconnect();

  const control = controlRef.value;
  if (!resizeObserver || !control) return;

  resizeObserver.observe(control);
  optionRefs.value?.forEach((button) => resizeObserver?.observe(button));
}

watch(
  () => props.options.map((option) => option.value),
  () => {
    reconnectResizeObserver();
    requestIndicatorUpdate();
  },
  { immediate: true },
);

watch(selectedIndex, () => requestIndicatorUpdate(), { immediate: true });

onMounted(() => {
  resizeObserver = new ResizeObserver(() => requestIndicatorUpdate());
  reconnectResizeObserver();
  requestIndicatorUpdate();
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();

  if (updateFrameId != null) {
    cancelAnimationFrame(updateFrameId);
  }
});
</script>

<template>
  <div
    :class="[
      'flex items-center gap-sm',
      isFullWidth ? 'w-full min-w-0' : isMobileFullWidth ? 'shrink-0 max-md:w-full max-md:min-w-0' : 'shrink-0',
    ]"
  >
    <div v-if="props.label">
      {{ props.label }}
    </div>
    <div
      ref="controlRef"
      :class="[
        isFullWidth
          ? 'w-full min-w-0'
          : isMobileFullWidth
            ? 'w-max shrink-0 max-md:w-full max-md:min-w-0 max-md:shrink'
            : 'w-max shrink-0',
        'relative inline-flex items-center rounded-button p-[3px]',
        props.theme === 'gray-surface' ? 'border border-divider bg-surface-primary/70' : 'bg-surface-secondary',
      ]"
      :data-theme="props.theme"
      role="group"
      :aria-label="props.ariaLabel"
    >
      <span
        aria-hidden="true"
        class="pointer-events-none absolute inset-y-[3px] left-0 rounded-button bg-surface-primary shadow-cookie-banner"
        :class="canAnimateIndicator && 'transition-[width,transform,opacity] duration-300 ease-out'"
        :style="indicatorStyle"
      />
      <button
        v-for="option in props.options"
        ref="optionRefs"
        :key="option.value"
        :aria-pressed="isSelected(option.value)"
        :aria-disabled="isOptionDisabled(option)"
        :disabled="isOptionDisabled(option)"
        type="button"
        :class="[
          'relative z-10 flex items-center justify-center rounded-button whitespace-nowrap min-w-0 overflow-hidden transition duration-300 focus-visible:outline-focus focus-visible:outline-offset-2 disabled:pointer-events-none disabled:text-secondary',
          props.width === 'content' && 'shrink-0',
          isMobileFullWidth && 'md:shrink-0 max-md:flex-1 max-md:basis-0',
          isSelected(option.value)
            ? 'text-primary font-semibold'
            : 'text-primary font-normal hover:bg-surface-primary/50 active:bg-surface-primary',
          isOptionDisabled(option) && !isSelected(option.value) ? 'opacity-50' : '',
          props.size === 'sm'
            ? 'h-[36px] px-md text-footer leading-[160%] tracking-[0.12px]'
            : 'h-button px-[20px] text-body leading-[170%]',
        ]"
        :style="isFullWidth ? { flex: '1 1 0' } : undefined"
        @click="onSelect(option.value)"
      >
        <slot :name="`option-${option.value}`" :option="option" :selected="isSelected(option.value)">
          <span class="block max-w-full min-w-0 truncate">{{ option.label }}</span>
        </slot>
      </button>
    </div>
  </div>
</template>
